import React, { ChangeEvent, FormEvent, useReducer, useState } from 'react';
import {
	TimkenGreaseSpecificationType,
	TimkenUnit,
} from '../../../../components/bearing-search/types/types';
import greaseFillBySpecsReducer, { initialFillBySpecsState } from './reducer';
import { bearingTypes } from '../../../../components/bearing-search/api/api';
import SelectDropdown from '../../../../components/bearing-search/components/forms/SelectDropdown';
import { greaseFillSpecTypes, text, unitList, unitMap } from '../../../data';
import { SelectOption } from '../../../types/types';
import NumberInput from '../../../../components/bearing-search/components/forms/NumberInput';
import { GreaseQuantityPartPayload } from '../InitialGreaseFill';
import { getPercentageErrorMessage } from '../../../utils/validations';

interface FillBySpecificationProps {
	onSubmit: ( payload: GreaseQuantityPartPayload ) => void;
	onClear: () => void;
}

const FillBySpecification: React.FC<FillBySpecificationProps> = ( {
	onSubmit,
	onClear,
} ) => {
	const [ isValidated, setIsValidated ] = useState( false );

	const [ formState, dispatch ] = useReducer(
		greaseFillBySpecsReducer,
		initialFillBySpecsState,
	);

	const selectBearingType = ( event: ChangeEvent<HTMLSelectElement> ) => {
		const partType = event.target.value as string;

		const selected = bearingTypes.find( ( type ) => type.partType === partType );

		if ( selected ) {
			dispatch( {
				type: 'change_bearing_type',
				payload: selected,
			} );
		}
	};

	const clearSearch = () => {
		setIsValidated( false );

		dispatch( {
			type: 'reset',
			payload: initialFillBySpecsState,
		} );

		onClear();
	};

	const handleFormChange = () => {
		setIsValidated( false );

		dispatch( {
			type: 'update_request_data',
		} );
	};

	const handleCalculate = ( event: FormEvent ) => {
		event.preventDefault();

		setIsValidated( true );

		if ( formState.requestData ) {
			onSubmit( formState.requestData );
		}
	};

	return (
		<div>
			<form onChange={handleFormChange} onSubmit={handleCalculate}>
				<div className="row form-row">
					<div className="col-md-3">
						<SelectDropdown
							id="initial_grease_fill_bearing_type"
							required={true}
							label={text.bearingType}
							value={formState.partType}
							options={bearingTypes.map( ( type ) => {
								return {
									id: type.partType,
									label: type.description,
									value: type.partType,
								} as SelectOption<string>;
							} )}
							onChange={selectBearingType}
						/>
					</div>

					<div className="col-md-2">
						<SelectDropdown
							id="initial_grease_fill_unit"
							name="unit"
							required={true}
							label={text.displayUnits}
							value={formState.unit}
							options={unitList}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_unit',
									payload: event.target.value as TimkenUnit,
								} )
							}
						/>
					</div>
				</div>

				<div className="row form-row">
					<div className="col-md-2">
						<NumberInput
							id="initial_grease_fill_inner_diameter"
							required={true}
							label={text.innerDiameter}
							unitLabel={unitMap[ formState.unit ].length}
							value={formState.innerDiameter}
							min={0}
							invalid={
								isValidated && ! formState.validation.get( 'innerDiameter' )
							}
							errorMessage={text.errorNonZero}
							onChange={( event: ChangeEvent<HTMLInputElement> ) =>
								dispatch( {
									type: 'change_number_field',
									payload: [ 'innerDiameter', event.target.value ],
								} )
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="initial_grease_fill_outer_diameter"
							required={true}
							label={text.outerDiameter}
							unitLabel={unitMap[ formState.unit ].length}
							value={formState.outerDiameter}
							min={0}
							invalid={
								isValidated && ! formState.validation.get( 'outerDiameter' )
							}
							errorMessage={text.errorNonZero}
							onChange={( event: ChangeEvent<HTMLInputElement> ) =>
								dispatch( {
									type: 'change_number_field',
									payload: [ 'outerDiameter', event.target.value ],
								} )
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="initial_grease_fill_width"
							required={true}
							label={text.bearingWidth}
							unitLabel={unitMap[ formState.unit ].length}
							value={formState.bearingWidth}
							min={0}
							invalid={isValidated && ! formState.validation.get( 'bearingWidth' )}
							errorMessage={text.errorNonZero}
							onChange={( event: ChangeEvent<HTMLInputElement> ) =>
								dispatch( {
									type: 'change_number_field',
									payload: [ 'bearingWidth', event.target.value ],
								} )
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="initial_grease_fill_weight"
							required={true}
							label={text.bearingWeight}
							unitLabel={unitMap[ formState.unit ].weight}
							value={formState.bearingWeight}
							min={0}
							invalid={
								isValidated && ! formState.validation.get( 'bearingWeight' )
							}
							errorMessage={text.errorNonZero}
							onChange={( event: ChangeEvent<HTMLInputElement> ) =>
								dispatch( {
									type: 'change_number_field',
									payload: [ 'bearingWeight', event.target.value ],
								} )
							}
						/>
					</div>
				</div>

				<div className="row form-row">
					<div className="col-md-3">
						<SelectDropdown
							id="initial_grease_fill_specs_type"
							required={true}
							label={text.specsType}
							value={formState.specsType}
							options={greaseFillSpecTypes}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_specs_type',
									payload: event.target.value as TimkenGreaseSpecificationType,
								} )
							}
						/>
					</div>
				</div>

				<div className="row form-row">
					{formState.specsType === 'Timken' && (
						<>
							<div className="col-md-3">
								<NumberInput
									id="initial_grease_fill_speed"
									required={true}
									label={text.speed}
									unitLabel="rpm"
									value={formState.speedDataID}
									min={0}
									invalid={
										isValidated && ! formState.validation.get( 'speedDataID' )
									}
									errorMessage={text.errorNonZero}
									onChange={( event: ChangeEvent<HTMLInputElement> ) =>
										dispatch( {
											type: 'change_number_field',
											payload: [ 'speedDataID', event.target.value ],
										} )
									}
								/>
							</div>
						</>
					)}

					{formState.specsType === 'Custom' && (
						<div className="col-md-3">
							<NumberInput
								id="initial_grease_fill_percentage"
								required={true}
								label={text.percentageFill}
								unitLabel="%"
								value={formState.percentageFillData}
								invalid={
									isValidated && ! formState.validation.get( 'percentageFillData' )
								}
								errorMessage={getPercentageErrorMessage(
									formState.percentageFillData,
								)}
								onChange={( event: ChangeEvent<HTMLInputElement> ) =>
									dispatch( {
										type: 'change_number_field',
										payload: [ 'percentageFillData', event.target.value ],
									} )
								}
							/>
						</div>
					)}
				</div>

				<div className="t-engineering-tools__search-actions">
					<button
						className="btn btn--primary"
						type="submit"
						disabled={isValidated}
					>
						{text.calculate}
					</button>

					<button
						className="btn btn--secondary"
						type="button"
						onClick={() => clearSearch()}
					>
						{text.clear}
					</button>
				</div>
			</form>
		</div>
	);
};

export default FillBySpecification;
